/**
 * Хук для загрузки и удаления фотографий в диагностике.
 * 
 * Применяется: Diagnostic
 */

import { useState } from 'react'
import { compressImage } from '../../../shared/lib/image'

export function usePhotoUpload(token) {
    const [photos, setPhotos] = useState({})
    const [uploadingKeys, setUploadingKeys] = useState({})
    const [failedUploads, setFailedUploads] = useState({})

    const initPhotos = (photosMap) => {
        setPhotos(photosMap)
    }

    const runUpload = async (key, operation, retry) => {
        setUploadingKeys(prev => ({ ...prev, [key]: true }))
        setFailedUploads(prev => {
            const next = { ...prev }
            delete next[key]
            return next
        })
        try {
            const result = await operation()
            return result
        } catch (error) {
            setFailedUploads(prev => ({ ...prev, [key]: { message: error.message || 'Не удалось загрузить фото', retry } }))
            return null
        } finally {
            setUploadingKeys(prev => {
                const next = { ...prev }
                delete next[key]
                return next
            })
        }
    }

    const uploadPhoto = async (itemId, side, file, overrideResultId = null) => {
        const key = `${itemId}_${side || 'none'}`
        const resultId = overrideResultId
        if (!resultId) return
        const retry = () => uploadPhoto(itemId, side, file, resultId)
        return runUpload(key, async () => {
            const fileToUpload = await compressImage(file)
            const formData = new FormData()
            formData.append('photo', fileToUpload)
            formData.append('result_id', resultId)
            const res = await fetch('/api/photos', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.message || 'Не удалось загрузить фото')
            const reader = new FileReader()
            reader.onload = (e) => {
                setPhotos(prev => ({
                    ...prev,
                    [key]: (prev[key] || []).some(photo => photo.id === data.id)
                        ? prev[key]
                        : [...(prev[key] || []), { id: data.id, url: e.target.result }]
                }))
            }
            reader.readAsDataURL(fileToUpload)
            return data
        }, retry)
    }

    const uploadOrderPhoto = async (file, orderId, photoType, key) => {
        const retry = () => uploadOrderPhoto(file, orderId, photoType, key)
        return runUpload(key, async () => {
            const fileToUpload = await compressImage(file)
            const formData = new FormData()
            formData.append('photo', fileToUpload)
            formData.append('order_id', orderId)
            formData.append('photo_type', photoType)
            const res = await fetch('/api/photos/order', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.message || 'Не удалось загрузить фото')
            const reader = new FileReader()
            reader.onload = (e) => {
                setPhotos(prev => ({
                    ...prev,
                    [key]: (prev[key] || []).some(photo => photo.id === data.id)
                        ? prev[key]
                        : [...(prev[key] || []), { id: data.id, url: e.target.result, storage: 'order' }]
                }))
            }
            reader.readAsDataURL(fileToUpload)
            return data
        }, retry)
    }

    const uploadMileagePhoto = (file, orderId) => uploadOrderPhoto(file, orderId, 'odometer', '15_none')
    const uploadAdditionalPhoto = (file, orderId) => uploadOrderPhoto(file, orderId, 'additional', 'additional_none')

    const deletePhoto = async (itemId, side, photoId, index) => {
        const key = `${itemId}_${side || 'none'}`
        try {
            if (photoId) {
                const photo = photos[key]?.[index]
                const endpoint = photo?.storage === 'order' ? `/api/photos/order/${photoId}` : `/api/photos/${photoId}`
                const response = await fetch(endpoint, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                })
                if (!response.ok) {
                    const data = await response.json()
                    throw new Error(data.message || 'Не удалось удалить фото')
                }
            }
            setPhotos(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }))
            return true
        } catch (err) {
            console.error('DELETE ERROR:', err)
            alert(err.message || 'Не удалось удалить фото')
            return false
        }
    }

    const getPhotos = (itemId, side) => {
        const key = `${itemId}_${side || 'none'}`
        return photos[key] || []
    }

    const retryUpload = (key) => failedUploads[key]?.retry?.()
    const hasUploadsInProgress = Object.keys(uploadingKeys).length > 0

    return {
        photos, setPhotos, initPhotos,
        uploadPhoto, uploadMileagePhoto, uploadAdditionalPhoto, deletePhoto, getPhotos,
        uploadingKeys, failedUploads, retryUpload, hasUploadsInProgress,
    }
}
